import {
  CreateOrUpdateScheduledExportContainer,
  DeleteExportDialog,
  DeleteReportDialog,
  RenameReportContainer,
  SaveExistingReportDialog,
  SaveNewReportContainer,
  ScheduledExportsDialog,
} from 'components/SavedReports/SavedReportsDialogs'
import type {
  DeleteScheduledExport,
  LoadDistributionLists,
  LoadScheduledExports,
  SaveScheduledExport,
  ValidationSchema,
} from 'components/SavedReports/SavedReportsDialogs'
import { SavedReport, ScheduledExport } from 'generated/graphql'
import useClipboard from 'hooks/useClipboard'
import { ReactNode, useReducer } from 'react'
import { useLocation } from 'react-router-dom'

type CreateReport<ReportConfig> = (input: {
  name: string
  reportConfig: ReportConfig
}) => Promise<SavedReport>

type UpdateReport<ReportConfig> = (input: {
  id: string
  name?: string
  reportConfig?: ReportConfig
}) => Promise<SavedReport>

type DeleteReport = (id: string) => Promise<SavedReport>

export type SavedReportsMenuActions = {
  openSaveNewReport: () => void
  openSaveReport: () => void
  revertToSavedReport: () => void
  openExport: () => void
  openScheduledExports: () => void
  shareReport: () => void
  openRenameReport: () => void
  openDeleteReport: () => void
  savedReportHasChanges: boolean
}

type ModalName =
  | 'saveNewReport'
  | 'saveReport'
  | 'renameReport'
  | 'deleteReport'
  | 'scheduledExportList'
  | 'createOrUpdateScheduledExport'
  | 'deleteExport'

type SavedReportsState = {
  saveNewReport: boolean
  saveReport: boolean
  renameReport: boolean
  deleteReport: boolean
  scheduledExportList: boolean
  createOrUpdateScheduledExport: boolean
  deleteExport: boolean
  selectedExport: Partial<ScheduledExport>
  returnToModal: ModalName | null
}

type SavedReportsAction =
  | { type: 'OPEN' | 'CLOSE'; payload: ModalName }
  | { type: 'SET_SELECTED_EXPORT'; payload: Partial<ScheduledExport> }
  | { type: 'SET_RETURN_TO_MODAL'; payload: ModalName | null }

const initialSavedReportsState: SavedReportsState = {
  saveNewReport: false,
  saveReport: false,
  renameReport: false,
  deleteReport: false,
  scheduledExportList: false,
  createOrUpdateScheduledExport: false,
  deleteExport: false,
  selectedExport: {},
  returnToModal: null,
}

function savedReportsReducer(state: SavedReportsState, action: SavedReportsAction): SavedReportsState {
  switch(action.type) {
    case 'OPEN':
      return { ...state, [action.payload]: true }
    case 'CLOSE':
      return { ...state, [action.payload]: false }
    case 'SET_SELECTED_EXPORT':
      return { ...state, selectedExport: action.payload }
    case 'SET_RETURN_TO_MODAL':
      return { ...state, returnToModal: action.payload }
    default:
      return state
  }
}

export type SavedReportsProps<ReportConfig> = {
  selectedReport: SavedReport | null
  initialName?: string
  getReportConfig: () => ReportConfig
  createReport: CreateReport<ReportConfig>
  updateReport: UpdateReport<ReportConfig>
  deleteReport: DeleteReport
  savedReportValidationSchema: ValidationSchema
  scheduledExportValidationSchema?: ValidationSchema
  loadScheduledExports?: LoadScheduledExports
  loadDistributionLists?: LoadDistributionLists
  saveScheduledExport?: SaveScheduledExport
  deleteScheduledExport?: DeleteScheduledExport
  hasChanges: () => boolean
  revertToSelectedSavedReport: () => void
  onReportCreated: (report: SavedReport) => void
  onReportUpdated: (report: SavedReport) => void
  onReportRenamed: (report: SavedReport) => void
  onReportDeleted: (report: SavedReport) => void
  onExport: () => void
  onMenuClose: () => void
  renderMenu: (actions: SavedReportsMenuActions) => ReactNode
}

export function SavedReports<ReportConfig>({
  selectedReport,
  initialName,
  getReportConfig,
  createReport,
  updateReport,
  deleteReport,
  savedReportValidationSchema,
  scheduledExportValidationSchema,
  loadScheduledExports,
  loadDistributionLists,
  saveScheduledExport,
  deleteScheduledExport,
  hasChanges,
  revertToSelectedSavedReport,
  onReportCreated,
  onReportUpdated,
  onReportRenamed,
  onReportDeleted,
  onExport,
  onMenuClose,
  renderMenu,
}: SavedReportsProps<ReportConfig>) {
  const [state, dispatch] = useReducer(savedReportsReducer, initialSavedReportsState)
  const location = useLocation()
  const { copyToClipboard } = useClipboard()

  const openModal = (modalName: ModalName, returnModalName: ModalName | null = null) => {
    dispatch({ type: 'OPEN', payload: modalName })
    dispatch({ type: 'SET_RETURN_TO_MODAL', payload: returnModalName })
    onMenuClose()
  }

  const closeModal = (modalName: ModalName, skipReturn = false) => {
    dispatch({ type: 'CLOSE', payload: modalName })
    if(state.returnToModal && !skipReturn) {
      dispatch({ type: 'OPEN', payload: state.returnToModal })
    }
    dispatch({ type: 'SET_RETURN_TO_MODAL', payload: null })
  }

  const openCreateOrUpdateScheduledExport = (exportData: Partial<ScheduledExport>) => {
    dispatch({ type: 'SET_SELECTED_EXPORT', payload: exportData })
    dispatch({ type: 'OPEN', payload: 'createOrUpdateScheduledExport' })
    dispatch({ type: 'CLOSE', payload: 'scheduledExportList' })
  }

  const openDeleteScheduledExport = (exportData: Partial<ScheduledExport>, returnModalName: ModalName) => {
    dispatch({ type: 'CLOSE', payload: 'scheduledExportList' })
    dispatch({ type: 'CLOSE', payload: 'createOrUpdateScheduledExport' })
    dispatch({ type: 'SET_SELECTED_EXPORT', payload: exportData })
    openModal('deleteExport', returnModalName)
  }

  const savedReportHasChanges = hasChanges()
  const fullUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`

  const menuActions: SavedReportsMenuActions = {
    openSaveNewReport: () => openModal('saveNewReport'),
    openSaveReport: () => openModal('saveReport'),
    revertToSavedReport: () => {
      revertToSelectedSavedReport()
      onMenuClose()
    },
    openExport: () => {
      onExport()
      onMenuClose()
    },
    openScheduledExports: () => openModal('scheduledExportList'),
    shareReport: () => {
      void copyToClipboard(
        fullUrl,
        `Link for "${selectedReport?.name}" has been copied`
      ).finally(onMenuClose)
    },
    openRenameReport: () => openModal('renameReport'),
    openDeleteReport: () => openModal('deleteReport'),
    savedReportHasChanges,
  }

  return(
    <>
      {renderMenu(menuActions)}

      {state.saveNewReport &&
        <SaveNewReportContainer
          initialName={initialName}
          onClose={() => closeModal('saveNewReport')}
          getReportConfig={getReportConfig}
          createReport={createReport}
          onReportCreated={onReportCreated}
          validationSchema={savedReportValidationSchema}
        />
      }
      {state.saveReport && selectedReport &&
        <SaveExistingReportDialog
          onClose={() => closeModal('saveReport')}
          selectedReport={selectedReport}
          getReportConfig={getReportConfig}
          updateReport={updateReport}
          onReportUpdated={onReportUpdated}
        />
      }
      {state.scheduledExportList && loadScheduledExports &&
        <ScheduledExportsDialog
          onClose={() => closeModal('scheduledExportList')}
          selectedReport={selectedReport}
          loadScheduledExports={loadScheduledExports}
          openCreateOrUpdateScheduledExport={openCreateOrUpdateScheduledExport}
          openDeleteScheduledExport={openDeleteScheduledExport}
        />
      }
      {state.createOrUpdateScheduledExport && loadDistributionLists && saveScheduledExport && scheduledExportValidationSchema &&
        <CreateOrUpdateScheduledExportContainer
          onClose={() => {
            closeModal('createOrUpdateScheduledExport')
            openModal('scheduledExportList')
          }}
          selectedReport={selectedReport}
          selectedExport={state.selectedExport}
          openDeleteScheduledExport={openDeleteScheduledExport}
          saveScheduledExport={saveScheduledExport}
          loadDistributionLists={loadDistributionLists}
          validationSchema={scheduledExportValidationSchema}
        />
      }
      {state.renameReport && selectedReport &&
        <RenameReportContainer
          onClose={() => closeModal('renameReport')}
          selectedReport={selectedReport}
          updateReport={updateReport}
          onReportRenamed={onReportRenamed}
          validationSchema={savedReportValidationSchema}
        />
      }
      {state.deleteReport && selectedReport &&
        <DeleteReportDialog
          onClose={() => closeModal('deleteReport')}
          selectedReport={selectedReport}
          deleteReport={deleteReport}
          onReportDeleted={onReportDeleted}
        />
      }
      {state.deleteExport && deleteScheduledExport &&
        <DeleteExportDialog
          onClose={() => closeModal('deleteExport')}
          onSuccess={() => closeModal('deleteExport', true)}
          selectedExport={state.selectedExport}
          unsetSelectedExport={() => dispatch({ type: 'SET_SELECTED_EXPORT', payload: {} })}
          deleteScheduledExport={deleteScheduledExport}
        />
      }
    </>
  )
}
